import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../App';
import { getLevelBadgeClass } from '../utils/level';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Users, MagnifyingGlass, PencilSimple, Eye, UploadSimple, FileText, Plus, CaretLeft, CaretRight, Trash, SealCheck, ArrowsClockwise } from '@phosphor-icons/react';
import MemberBadge from '../components/MemberBadge';
import { levelTestsThisYear } from '../components/LevelTestPicker';
import { UserRegistrationStats } from '../components/UserRegistrationStats';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { useCardPayment } from '../lib/cardTerminal';
import { PaymentMethodPicker } from '../components/PaymentMethodPicker';

const AdminUsers = () => {
  const { user: currentUser, t, language, API } = useAuth();
  const payByCard = useCardPayment();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const L = (en, deu) => language === 'de' ? deu : en;
  
  const [users, setUsers] = useState([]);
  const [payPick, setPayPick] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  // 'name' (alphabetical, default) or 'newest' (created_at DESC).
  const [sortMode, setSortMode] = useState('name');

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', role: '', player_level: '', credits_balance: '' });

  // Add new client modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [addLoading, setAddLoading] = useState(false);

  // Import
  const [showImportModal, setShowImportModal] = useState(false);
  const [importPreview, setImportPreview] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const loadUsers = useCallback(async (p = page, q = searchQuery, s = sortMode) => {
    try {
      setLoading(true);
      const { data } = await API.get(`/users?page=${p}&limit=50&q=${encodeURIComponent(q)}&sort=${s}`);
      // Defensive: an unexpected server response (auth expiry, proxy hiccup)
      // used to drop `users` to undefined, which then crashed the `.map` render
      // and painted a blank white page. Coerce to an array so the table just
      // renders empty instead.
      setUsers(Array.isArray(data?.users) ? data.users : []);
      setTotalPages(data?.pages || 1);
      setTotalUsers(data?.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [API, page, searchQuery, sortMode]);

  useEffect(() => { loadUsers(page, searchQuery, sortMode); }, [page, sortMode]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      loadUsers(1, searchQuery, sortMode);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Auto-open user profile when navigated with ?user_id=...
  useEffect(() => {
    const userId = searchParams.get('user_id');
    if (!userId) return;
    (async () => {
      try {
        const { data } = await API.get(`/users/${userId}`);
        const u = data?.user || data;
        if (u && u.id) {
          openEdit(u);
          // clear the param so refreshing the page doesn't auto-reopen
          searchParams.delete('user_id');
          setSearchParams(searchParams, { replace: true });
        }
      } catch (e) {
        toast.error(L('User not found', 'Kunde nicht gefunden'));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('user_id')]);

  // Subscription state — price defaults come from the public website pricing.
  //   Court: 58 CHF off-peak / 68 CHF peak (per hour)
  //   Training: TRAINING_PRICE_MATRIX (per participant, 60min slot)
  // Admin can override the auto-filled price via the input.
  const COURT_PRICE = { off_peak: 58, peak: 68 };
  const TRAINING_MATRIX_60 = {
    1: { off_peak: 120, peak: 140 },
    2: { off_peak: 70,  peak: 80  },
    3: { off_peak: 55,  peak: 70  },
    4: { off_peak: 50,  peak: 60  },
  };
  const computeSubDefaultPrice = (type, isPeak, participants) => {
    const tier = isPeak ? 'peak' : 'off_peak';
    if (type === 'court') return COURT_PRICE[tier];
    const row = TRAINING_MATRIX_60[participants] || TRAINING_MATRIX_60[1];
    return row[tier];
  };
  const [userSubs, setUserSubs] = useState([]);
  const [showSellSub, setShowSellSub] = useState(false);
  const [subForm, setSubForm] = useState({
    type: 'training',
    paid_lessons: '10',
    bonus_lessons: '1',
    price_per_lesson: '120',
    payment_method: 'card',
    is_peak: false,
    participants: 1,
  });
  const [subLoading, setSubLoading] = useState(false);
  const [subDetail, setSubDetail] = useState(null);
  const [subDetailLoading, setSubDetailLoading] = useState(false);

  const openSubDetail = async (subId) => {
    try {
      setSubDetailLoading(true);
      setSubDetail({ id: subId, loading: true });
      const { data } = await API.get(`/subscriptions/${subId}`);
      setSubDetail(data);
    } catch (e) {
      toast.error(L('Failed to load subscription', 'Abo konnte nicht geladen werden'));
      setSubDetail(null);
    } finally { setSubDetailLoading(false); }
  };

  const loadUserSubscriptions = useCallback(async (userId) => {
    if (!userId) return;
    try {
      const { data } = await API.get(`/subscriptions?user_id=${userId}`);
      setUserSubs(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
  }, [API]);

  const handleSellSubscription = async () => {
    if (!selectedUser) return;
    try {
      setSubLoading(true);
      const isYearly = subForm.type === 'golf_yearly_solo' || subForm.type === 'golf_yearly_group';
      const payload = {
        user_id: selectedUser.id,
        type: subForm.type,
        payment_method: subForm.payment_method,
      };
      if (isYearly) {
        payload.total_price_override = parseFloat(subForm.price_per_lesson) || 0;
      } else {
        const paid = parseInt(subForm.paid_lessons) || 0;
        const bonus = parseInt(subForm.bonus_lessons) || 0;
        payload.total_lessons = paid + bonus;
        payload.paid_lessons = paid;
        payload.price_per_lesson = parseFloat(subForm.price_per_lesson);
      }
      if (subForm.payment_method === 'card') {
        const total = isYearly ? payload.total_price_override : (payload.paid_lessons || 0) * (payload.price_per_lesson || 0);
        const term = await payByCard(total, `Abo ${selectedUser.name}`);
        if (!term.ok) { toast.error(`${L('Card payment not completed', 'Kartenzahlung nicht abgeschlossen')}: ${term.reason} — ${L('abo not sold', 'Abo nicht verkauft')}`, { duration: 10000 }); return; }
      }
      await API.post('/subscriptions', payload);
      toast.success(L('Subscription sold and added to cash register', 'Abo verkauft und in Kasse erfasst'));
      setShowSellSub(false);
      setSubForm({
        type: 'training',
        paid_lessons: '10',
        bonus_lessons: '1',
        price_per_lesson: '120',
        payment_method: 'cash',
        is_peak: false,
        participants: 1,
      });
      loadUserSubscriptions(selectedUser.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Failed', 'Fehlgeschlagen'));
    } finally { setSubLoading(false); }
  };

  const handleDeleteUser = async (user) => {
    if (!user?.id) return;
    if (!window.confirm(L(
      `Delete ${user.name}? All personal data (name, email, phone, address, level, notes) will be erased permanently. Bookings and cash entries stay anonymised ("Deleted customer") for accounting.`,
      `${user.name} löschen? Alle persönlichen Daten (Name, E-Mail, Telefon, Adresse, Level, Notizen) werden endgültig entfernt. Buchungen und Kasseneinträge bleiben anonym („Gelöschter Kunde“) für die Buchhaltung.`
    ))) return;
    if (!window.confirm(L(
      `Are you sure? ${user.name} will be deleted PERMANENTLY and cannot be restored.`,
      `Bist du sicher? ${user.name} wird ENDGÜLTIG gelöscht und kann nicht wiederhergestellt werden.`
    ))) return;
    try {
      await API.delete(`/admin/users/${user.id}`);
      toast.success(L('Customer permanently deleted', 'Kunde endgültig gelöscht'));
      loadUsers(page, searchQuery, sortMode);
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Delete failed', 'Löschen fehlgeschlagen'), { duration: 10000 });
    }
  };

  const handleDeleteSubscription = async (subId) => {
    if (!window.confirm(L('Delete this subscription? The sale entry will also be removed.', 'Dieses Abo löschen? Der Verkaufseintrag wird ebenfalls entfernt.'))) return;
    try {
      await API.delete(`/subscriptions/${subId}`);
      toast.success(L('Deleted', 'Gelöscht'));
      loadUserSubscriptions(selectedUser.id);
    } catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
  };

  const toggleMembership = async (user) => {
    try {
      const next = !user.is_member;
      await API.put(`/users/${user.id}/membership`, { is_member: next });
      toast.success(next
        ? L(`${user.name} is now a member`, `${user.name} ist jetzt Mitglied`)
        : L(`Membership revoked for ${user.name}`, `Mitgliedschaft von ${user.name} aufgehoben`));
      loadUsers(page, searchQuery);
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
    }
  };

  const openEdit = (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name || '', email: user.email || '', phone: user.phone || '',
      role: user.role || 'customer', player_level: String(user.player_level || 3),
      credits_balance: String(user.credits_balance || 0),
    });
    setShowEditModal(true);
    loadUserSubscriptions(user.id);
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;
    try {
      const payload = {};
      if (editForm.name !== selectedUser.name) payload.name = editForm.name;
      if (editForm.email !== selectedUser.email) payload.email = editForm.email;
      if (editForm.phone !== (selectedUser.phone || '')) payload.phone = editForm.phone;
      if (editForm.role !== selectedUser.role) payload.role = editForm.role;
      if (parseFloat(editForm.player_level) !== selectedUser.player_level) payload.player_level = parseFloat(editForm.player_level);
      if (parseFloat(editForm.credits_balance) !== (selectedUser.credits_balance || 0)) payload.credits_balance = parseFloat(editForm.credits_balance);
      if (Object.keys(payload).length === 0) { toast.info(L('No changes', 'Keine Änderungen')); setShowEditModal(false); return; }
      await API.put(`/users/${selectedUser.id}`, payload);
      toast.success(L('User updated!', 'Benutzer aktualisiert!'));
      setShowEditModal(false);
      loadUsers(page, searchQuery);
    } catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
  };

  const handleAddClient = async () => {
    if (!addForm.name || !addForm.email) return;
    setAddLoading(true);
    try {
      const { data } = await API.post('/admin/users/import', {
        users: [{ name: addForm.name, email: addForm.email, phone: addForm.phone }],
        default_password: addForm.password || null
      });
      if (!data.imported) { toast.error(L('Email already exists', 'E-Mail existiert bereits')); return; }
      if (data.temp_password) {
        toast.success(`${L('Client added! One-time password', 'Kunde hinzugefügt! Einmal-Passwort')}: ${data.temp_password}`, { duration: 60000, closeButton: true });
      } else {
        toast.success(L('Client added!', 'Kunde hinzugefügt!'));
      }
      setShowAddModal(false);
      setAddForm({ name: '', email: '', phone: '', password: '' });
      loadUsers(page, searchQuery);
    } catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
    finally { setAddLoading(false); }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length === 0) return;
      const sep = lines[0].includes(';') ? ';' : lines[0].includes('\t') ? '\t' : ',';
      const header = lines[0].split(sep).map(h => h.trim().toLowerCase().replace(/['"]/g, ''));
      const nameIdx = header.findIndex(h => h.includes('name') || h.includes('vorname'));
      const emailIdx = header.findIndex(h => h.includes('email') || h.includes('e-mail'));
      const phoneIdx = header.findIndex(h => h.includes('phone') || h.includes('handy') || h.includes('telefon'));
      if (emailIdx === -1) { toast.error(L('CSV must have an "Email" column', 'CSV muss eine "Email"-Spalte haben')); return; }
      const parsed = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(sep).map(c => c.trim().replace(/^['"]|['"]$/g, ''));
        const email = cols[emailIdx];
        if (!email || !email.includes('@')) continue;
        parsed.push({ name: nameIdx >= 0 ? cols[nameIdx] : email.split('@')[0], email, phone: phoneIdx >= 0 ? cols[phoneIdx] || '' : '' });
      }
      setImportPreview(parsed);
      setImportResult(null);
      setShowImportModal(true);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const executeImport = async () => {
    if (importPreview.length === 0) return;
    setImportLoading(true);
    try {
      const { data } = await API.post('/admin/users/import', { users: importPreview, default_password: '' });
      setImportResult(data);
      toast.success(`${data.imported} ${L('users imported', 'Benutzer importiert')}`);
      loadUsers(page, searchQuery);
    } catch (e) { toast.error(e.response?.data?.detail || L('Import failed', 'Import fehlgeschlagen')); }
    finally { setImportLoading(false); }
  };

  const getLevelBadge = (level) => getLevelBadgeClass(level);

  // Community-signup block: clears the flag and (optionally) logs the
  // settlement into Kasse using the chosen payment method. Keep the UX
  // ultra-simple — a numeric prompt, same pattern as the mark-paid-at-club
  // flow — so reception can settle a debt in one tap without leaving the
  // users table.
  const clearCommunityBlock = async (u) => {
    const debt = Number(u.community_signup_debt_amount || 0).toFixed(2);
    const methods = [
      { key: 'cash',    label: L('Cash', 'Bar') },
      { key: 'card',    label: L('Card', 'Karte') },
      { key: 'credits', label: L('Credits (CSS)', 'CSS-Guthaben') },
      { key: 'invoice', label: L('Invoice', 'Rechnung') },
      { key: 'waive',   label: L('Waive (no charge)', 'Erlassen (kein Betrag)') },
    ];
    setPayPick({
      title: L(`${u.name} owes CHF ${debt}`, `${u.name} schuldet CHF ${debt}`),
      subtitle: L('How was it settled?', 'Wie wurde beglichen?'),
      methods,
      run: (chosen) => settleCommunityBlock(u, chosen),
    });
  };

  const settleCommunityBlock = async (u, chosen) => {
    try {
      const { data } = await API.post(`/admin/users/${u.id}/community-block/clear`, { payment_method: chosen.key });
      const chf = Number(data.debt_amount_settled || 0).toFixed(2);
      if (chosen.key === 'waive') {
        toast.success(L(`Block cleared — CHF ${chf} waived.`, `Sperre aufgehoben — CHF ${chf} erlassen.`));
      } else {
        toast.success(L(`Block cleared — CHF ${chf} via ${chosen.label}, logged to Kasse.`,
                        `Sperre aufgehoben — CHF ${chf} (${chosen.label}) in Kasse verbucht.`));
      }
      setPayPick(null);
      loadUsers(page, searchQuery, sortMode);
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
    }
  };
  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin': return 'bg-emerald-500/20 text-emerald-600';
      case 'staff': return 'bg-blue-50 text-blue-600';
      case 'trainer': return 'bg-purple-50 text-purple-600';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-users">
      <PaymentMethodPicker request={payPick} onPick={(m) => payPick?.run(m)} onClose={() => setPayPick(null)} cancelLabel={L('Cancel', 'Abbrechen')} />
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2">{t('users')}</h1>
          <p className="text-slate-500">{totalUsers.toLocaleString()} {L('users total', 'Benutzer gesamt')}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input type="file" ref={fileInputRef} accept=".csv,.txt" className="hidden" onChange={handleFileSelect} data-testid="csv-file-input" />
          <Button
            onClick={async () => {
              try {
                const { data } = await API.post('/admin/matches/backfill-member-tick');
                toast.success(L(
                  `Synced ${data.users_touched || 0} users · ${data.matches_updated || 0} matches · ${data.events_updated || 0} mixins`,
                  `${data.users_touched || 0} Kunden synchronisiert · ${data.matches_updated || 0} Matches · ${data.events_updated || 0} Mixins`,
                ));
              } catch (e) {
                toast.error(e.response?.data?.detail || L('Failed', 'Fehlgeschlagen'));
              }
            }}
            variant="outline"
            className="border-slate-200 text-slate-600 hover:bg-slate-50 font-medium gap-2"
            title={L(
              'Refresh level, rating and membership badge on all future signups',
              'Level, Rating und Mitgliedschafts-Häkchen auf allen zukünftigen Anmeldungen aktualisieren',
            )}
            data-testid="resync-signups-btn"
          >
            <ArrowsClockwise size={16} /> {L('Resync Signups', 'Anmeldungen sync')}
          </Button>
          <Button onClick={() => fileInputRef.current?.click()} variant="outline" className="border-slate-200 text-slate-600 hover:bg-slate-50 font-medium gap-2" data-testid="import-csv-btn">
            <UploadSimple size={16} /> {L('CSV Import', 'CSV Import')}
          </Button>
          <Button onClick={() => { setAddForm({ name: '', email: '', phone: '', password: '' }); setShowAddModal(true); }}
            className="bg-emerald-500 text-white hover:bg-emerald-600 font-bold gap-2" data-testid="add-client-btn">
            <Plus size={16} weight="bold" /> {L('Add Client', 'Kunde hinzufügen')}
          </Button>
        </div>
      </div>

      {currentUser?.role === 'admin' && <UserRegistrationStats API={API} L={L} />}

      {/* Search + sort */}
      <div className="card-static p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-slate-50 border-slate-200 text-slate-900"
              placeholder={L('Search by name, email, phone or reference...', 'Nach Name, E-Mail, Telefon oder Referenz suchen...')}
              data-testid="search-users" />
          </div>
          {/* Sort toggle — Feb 2026: expanded from A-Z / Newest to also
              cover Level (highest first) and Verified (blue-tick first)
              since staff often needs to scan by skill or membership. */}
          <div className="inline-flex flex-wrap rounded-lg border border-slate-200 overflow-hidden text-sm" data-testid="user-sort-toggle">
            {[
              { k: 'name',       en: 'A–Z',                 de: 'A–Z' },
              { k: 'newest',     en: 'Newest first',        de: 'Neueste zuerst' },
              { k: 'level_desc', en: 'Level ↓',             de: 'Level ↓' },
              { k: 'verified',   en: 'Verified first',      de: 'Verifiziert zuerst' },
            ].map((opt, idx) => (
              <button
                key={opt.k}
                type="button"
                onClick={() => { setPage(1); setSortMode(opt.k); }}
                className={`px-3 py-1.5 font-semibold transition-colors ${idx > 0 ? 'border-l border-slate-200' : ''} ${sortMode === opt.k ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                data-testid={`sort-users-${opt.k}`}
              >
                {L(opt.en, opt.de)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card-static overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">
            {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-14 bg-slate-100 animate-pulse rounded-lg" />)}
          </div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center">
            <Users size={48} className="text-slate-500 mx-auto mb-4" weight="duotone" />
            <p className="text-slate-500">{L('No users found', 'Keine Benutzer gefunden')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-[#F8FAFC]">
                  <th className="text-left py-3 px-4 label-text">{L('Ref.', 'Ref.')}</th>
                  <th className="text-left py-3 px-4 label-text">{t('name')}</th>
                  <th className="text-left py-3 px-4 label-text hidden sm:table-cell">{t('email')}</th>
                  <th className="text-left py-3 px-4 label-text hidden md:table-cell">{L('Phone', 'Telefon')}</th>
                  <th className="text-left py-3 px-4 label-text hidden lg:table-cell">{L('Role', 'Rolle')}</th>
                  <th className="text-left py-3 px-4 label-text">{t('level')}</th>
                  <th className="text-right py-3 px-4 label-text">{L('Actions', 'Aktionen')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`user-row-${user.id}`}>
                    <td className="py-3 px-4">
                      <span className="text-xs font-mono text-slate-400">{user.reference_number || '-'}</span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-white border border-slate-200 rounded-lg flex items-center justify-center shrink-0">
                          <span className="text-emerald-600 font-bold text-xs">{user.name?.charAt(0)?.toUpperCase()}</span>
                        </div>
                        <div className="min-w-0">
                          <span className="text-slate-900 font-medium text-sm flex items-center gap-1.5 truncate">
                            {user.name}
                            <MemberBadge user={user} size={13} />
                            {user.is_junior && (
                              <span
                                className="px-1.5 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300"
                                title={L('Junior — directory entry only, cannot log in', 'Junior — nur im Verzeichnis, kein Login')}
                                data-testid={`junior-badge-${user.id}`}
                              >
                                Junior
                              </span>
                            )}
                            {user.community_signup_blocked_at && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); clearCommunityBlock(user); }}
                                className="px-1.5 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-red-100 text-red-700 border border-red-300 hover:bg-red-200 transition-colors whitespace-nowrap"
                                title={L(
                                  `Blocked — owes CHF ${Number(user.community_signup_debt_amount||0).toFixed(2)}. Click to settle & unblock.`,
                                  `Gesperrt — schuldet CHF ${Number(user.community_signup_debt_amount||0).toFixed(2)}. Klick zum Abrechnen & Entsperren.`
                                )}
                                data-testid={`community-block-${user.id}`}
                              >
                                💳 CHF {Number(user.community_signup_debt_amount||0).toFixed(0)}
                              </button>
                            )}
                          </span>
                          {user.is_member && user.member_since && (
                            <span className="text-sky-600 text-[11px] block" data-testid={`member-since-${user.id}`}>
                              {L('Member since', 'Mitglied seit')} {user.member_since.split('-').reverse().join('.')}
                            </span>
                          )}
                          <span className="text-slate-400 text-xs sm:hidden block truncate">
                            {user.is_junior ? (user.parent_email || L('No login', 'Kein Login')) : user.email}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-slate-500 text-sm hidden sm:table-cell">
                      {user.is_junior ? (
                        <span className="italic text-slate-400" title={user.email}>
                          {user.parent_email || L('No login', 'Kein Login')}
                        </span>
                      ) : user.email}
                    </td>
                    <td className="py-3 px-4 text-slate-500 text-sm hidden md:table-cell">{user.phone || '-'}</td>
                    <td className="py-3 px-4 hidden lg:table-cell">
                      <span className={`px-2 py-0.5 text-xs font-bold uppercase rounded-lg ${getRoleBadge(user.role)}`}>{user.role}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-slate-900 font-bold text-sm">{user.player_level}</span>
                      {levelTestsThisYear(user) > 0 && (
                        <span className="ml-2 px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200 whitespace-nowrap"
                          title={L('Level tests this year', 'Level-Tests dieses Jahr')} data-testid={`level-tests-${user.id}`}>
                          LT {levelTestsThisYear(user)}/3
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => toggleMembership(user)}
                          title={user.is_member ? L('Revoke membership', 'Mitgliedschaft entfernen') : L('Mark as member', 'Als Mitglied markieren')}
                          className={`h-8 px-2 ${user.is_member ? 'text-sky-600 hover:bg-sky-50' : 'text-slate-400 hover:bg-slate-100'}`}
                          data-testid={`toggle-member-${user.id}`}>
                          <SealCheck size={16} weight={user.is_member ? 'fill' : 'regular'} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/users/${user.id}`)}
                          className="text-blue-600 hover:bg-blue-50 h-8 px-2" data-testid={`view-user-${user.id}`}>
                          <Eye size={16} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(user)}
                          className="text-slate-600 hover:bg-slate-100 h-8 px-2" data-testid={`edit-user-${user.id}`}>
                          <PencilSimple size={16} />
                        </Button>
                        {/* Delete — admin + trainer; the backend rejects any non-customer target,
                            so this button is safely limited to customer rows only. */}
                        {(currentUser?.role === 'admin' || currentUser?.role === 'trainer') && user.role === 'customer' && user.id !== currentUser?.id && (
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(user)}
                            className="text-red-500 hover:bg-red-50 h-8 px-2" data-testid={`delete-user-${user.id}`}
                            title={L('Delete user', 'Benutzer löschen')}>
                            <Trash size={16} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
            <p className="text-sm text-slate-500">
              {L(`Page ${page} of ${totalPages}`, `Seite ${page} von ${totalPages}`)} ({totalUsers.toLocaleString()} {L('total', 'gesamt')})
            </p>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                className="border-slate-200 text-slate-600 h-8" data-testid="prev-page">
                <CaretLeft size={16} />
              </Button>
              {/* Show page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let p;
                if (totalPages <= 5) p = i + 1;
                else if (page <= 3) p = i + 1;
                else if (page >= totalPages - 2) p = totalPages - 4 + i;
                else p = page - 2 + i;
                return (
                  <Button key={p} variant={p === page ? 'default' : 'outline'} size="sm" onClick={() => setPage(p)}
                    className={`h-8 w-8 ${p === page ? 'bg-emerald-500 text-white' : 'border-slate-200 text-slate-600'}`}>
                    {p}
                  </Button>
                );
              })}
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                className="border-slate-200 text-slate-600 h-8" data-testid="next-page">
                <CaretRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Add Client Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md" data-testid="add-client-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{L('Add New Client', 'Neuen Kunden hinzufügen')}</DialogTitle>
            <DialogDescription className="text-slate-500">{L('Create a customer account', 'Kundenkonto erstellen')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="label-text">{t('name')} *</Label>
              <Input value={addForm.name} onChange={(e) => setAddForm({...addForm, name: e.target.value})}
                className="bg-slate-50 border-slate-200" placeholder="Max Muster" data-testid="add-client-name" />
            </div>
            <div className="space-y-2">
              <Label className="label-text">{t('email')} *</Label>
              <Input type="email" value={addForm.email} onChange={(e) => setAddForm({...addForm, email: e.target.value})}
                className="bg-slate-50 border-slate-200" placeholder="max@example.com" data-testid="add-client-email" />
            </div>
            <div className="space-y-2">
              <Label className="label-text">{L('Phone', 'Telefon')}</Label>
              <Input value={addForm.phone} onChange={(e) => setAddForm({...addForm, phone: e.target.value})}
                className="bg-slate-50 border-slate-200" placeholder="+41 79 123 4567" data-testid="add-client-phone" />
            </div>
            <div className="space-y-2">
              <Label className="label-text">{L('Password', 'Passwort')}</Label>
              <Input value={addForm.password} onChange={(e) => setAddForm({...addForm, password: e.target.value})}
                className="bg-slate-50 border-slate-200" data-testid="add-client-password" />
              <p className="text-xs text-slate-400">{L('Leave empty → a one-time password is generated. The client must change it at first login.', 'Leer lassen → ein Einmal-Passwort wird erzeugt. Der Kunde muss es beim ersten Login ändern.')}</p>
            </div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowAddModal(false)} className="flex-1 border-slate-200 text-slate-600">{t('cancel')}</Button>
              <Button onClick={handleAddClient} disabled={addLoading || !addForm.name || !addForm.email}
                className="flex-1 bg-emerald-500 text-white hover:bg-emerald-600 font-bold" data-testid="save-add-client">
                {addLoading ? '...' : L('Add Client', 'Kunde hinzufügen')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md max-h-[90vh] overflow-y-auto" data-testid="edit-user-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{L('Edit User', 'Benutzer bearbeiten')}</DialogTitle>
            <DialogDescription className="text-slate-500">{selectedUser?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {currentUser?.role === 'trainer' && (
              <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-800" data-testid="trainer-scope-hint">
                {L('As a trainer you can update name, email, phone and player level. Role and credits are managed by admin/reception.',
                   'Als Trainer kannst du Name, E-Mail, Telefon und Spielerlevel anpassen. Rolle und Guthaben werden vom Admin/Empfang verwaltet.')}
              </div>
            )}
            <div className="space-y-2">
              <Label className="label-text">{t('name')} *</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                className="bg-slate-50 border-slate-200 disabled:opacity-60 disabled:cursor-not-allowed" data-testid="edit-user-name" />
            </div>
            <div className="space-y-2">
              <Label className="label-text">{t('email')} *</Label>
              <Input type="email" value={editForm.email} onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                className="bg-slate-50 border-slate-200 disabled:opacity-60 disabled:cursor-not-allowed" data-testid="edit-user-email" />
            </div>
            <div className="space-y-2">
              <Label className="label-text">{L('Phone', 'Telefon')}</Label>
              <Input value={editForm.phone} onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                className="bg-slate-50 border-slate-200 disabled:opacity-60 disabled:cursor-not-allowed" placeholder="+41 79 123 4567" data-testid="edit-user-phone" />
            </div>
            {/* Role picker — admin only (staff & trainer cannot change roles). */}
            {currentUser?.role === 'admin' && (
              <div className="space-y-2">
                <Label className="label-text">{L('Role', 'Rolle')}</Label>
                <Select value={editForm.role} onValueChange={(v) => setEditForm({...editForm, role: v})}>
                  <SelectTrigger className="bg-slate-50 border-slate-200" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="customer">Customer</SelectItem>
                    <SelectItem value="trainer">Trainer</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-text">{t('level')}</Label>
                <Select value={editForm.player_level} onValueChange={(v) => setEditForm({...editForm, player_level: v})}>
                  <SelectTrigger className="bg-slate-50 border-slate-200" data-testid="edit-user-level"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 max-h-48">
                    {[1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7].map(l => (
                      <SelectItem key={l} value={l.toString()}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Credits — admin/staff only. Trainers cannot see or edit the balance. */}
              {currentUser?.role !== 'trainer' && (
                <div className="space-y-2">
                  <Label className="label-text">{t('credits')} (CHF)</Label>
                  <Input type="number" value={editForm.credits_balance} onChange={(e) => setEditForm({...editForm, credits_balance: e.target.value})}
                    className="bg-slate-50 border-slate-200" data-testid="edit-user-credits" />
                </div>
              )}
            </div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowEditModal(false)} className="flex-1 border-slate-200 text-slate-600">{t('cancel')}</Button>
              <Button onClick={handleSaveUser} className="flex-1 bg-emerald-500 text-white hover:bg-emerald-600 font-bold" data-testid="save-user-btn">
                {L('Save Changes', 'Änderungen speichern')}
              </Button>
            </div>

            {/* Subscriptions / Abos section */}
            {selectedUser && selectedUser.role === 'customer' && (
              <div className="border-t border-slate-200 pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-slate-800 uppercase">{L('Subscriptions / Abos', 'Abos')}</h3>
                  <Button onClick={() => setShowSellSub(true)} className="bg-purple-500 text-white hover:bg-purple-600 h-8 px-3 text-xs font-bold rounded-lg" data-testid="sell-sub-btn">
                    + {L('Sell Abo', 'Abo verkaufen')}
                  </Button>
                </div>
                {userSubs.length === 0 ? (
                  <p className="text-sm text-slate-400 italic">{L('No subscriptions yet', 'Noch keine Abos')}</p>
                ) : (
                  <div className="space-y-2">
                    {userSubs.map(s => {
                      const totalLessons = s.total_lessons || 0;
                      const usedLessons = s.used_lessons || 0;
                      const pricePerLesson = s.price_per_lesson || 0;
                      const remaining = totalLessons - usedLessons;
                      const pct = totalLessons > 0 ? (usedLessons / totalLessons) * 100 : 0;
                      const exhausted = remaining <= 0;
                      const remainingChf = remaining * pricePerLesson;
                      const totalChf = totalLessons * pricePerLesson;
                      return (
                        <div
                          key={s.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => openSubDetail(s.id)}
                          onKeyDown={(e) => { if (e.key === 'Enter') openSubDetail(s.id); }}
                          className={`border rounded-xl p-3 cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 ${exhausted ? 'bg-slate-50 border-slate-200 opacity-70' : s.type === 'training' ? 'bg-blue-50 border-blue-200 hover:bg-blue-100' : 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100'}`}
                          data-testid={`sub-row-${s.id}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <div>
                              <span className="font-bold text-sm">{s.type === 'training' ? L('Training Abo', 'Training-Abo') : L('Court Abo', 'Court-Abo')}</span>
                              <span className="ml-2 text-xs text-slate-500">{pricePerLesson.toFixed(2)} CHF/Lekt.</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-base font-extrabold ${exhausted ? 'text-red-500' : 'text-slate-900'}`}>{remaining}/{totalLessons}</span>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDeleteSubscription(s.id); }}
                                className="p-1 text-red-400 hover:bg-red-50 rounded"
                                data-testid={`del-sub-${s.id}`}
                              >
                                <Trash size={14} />
                              </button>
                            </div>
                          </div>
                          <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
                            <div className={`h-full ${exhausted ? 'bg-red-400' : s.type === 'training' ? 'bg-blue-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(100, pct)}%` }} />
                          </div>
                          <div className="flex items-center justify-between mt-2 text-xs text-slate-600">
                            <span>{L('Remaining value', 'Restwert')}: <strong className="text-slate-800">{remainingChf.toFixed(0)} CHF</strong> / {totalChf.toFixed(0)} CHF</span>
                            <span className="text-slate-400">{L('Click for details', 'Details anzeigen')} →</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Sell Subscription Modal */}
      <Dialog open={showSellSub} onOpenChange={setShowSellSub}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-sm rounded-2xl" data-testid="sell-sub-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{L('Sell Subscription', 'Abo verkaufen')}</DialogTitle>
            <DialogDescription>{selectedUser?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => setSubForm({
                  ...subForm,
                  type: 'training',
                  price_per_lesson: String(computeSubDefaultPrice('training', subForm.is_peak, subForm.participants)),
                })}
                className={`px-3 py-2 rounded-lg text-sm font-bold border ${subForm.type === 'training' ? 'bg-blue-500 text-white border-blue-500' : 'bg-white border-slate-200 text-slate-600'}`}
                data-testid="sub-type-training">
                {L('Training', 'Training')}
              </button>
              <button
                onClick={() => setSubForm({
                  ...subForm,
                  type: 'court',
                  price_per_lesson: String(computeSubDefaultPrice('court', subForm.is_peak, subForm.participants)),
                })}
                className={`px-3 py-2 rounded-lg text-sm font-bold border ${subForm.type === 'court' ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white border-slate-200 text-slate-600'}`}
                data-testid="sub-type-court">
                {L('Court', 'Court')}
              </button>
              <button
                onClick={() => setSubForm({
                  ...subForm,
                  type: 'golf_yearly_solo',
                  price_per_lesson: '999',
                })}
                className={`px-3 py-2 rounded-lg text-sm font-bold border ${(subForm.type === 'golf_yearly_solo' || subForm.type === 'golf_yearly_group') ? 'bg-amber-500 text-white border-amber-500' : 'bg-white border-slate-200 text-slate-600'}`}
                data-testid="sub-type-golf-yearly">
                {L('Golf Yearly', 'Golf Jahr')}
              </button>
            </div>

            {(subForm.type === 'golf_yearly_solo' || subForm.type === 'golf_yearly_group') ? (
              <>
                {/* Yearly sub-type + price */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setSubForm({ ...subForm, type: 'golf_yearly_solo', price_per_lesson: '999' })}
                    className={`px-3 py-2 rounded-lg text-sm font-bold border ${subForm.type === 'golf_yearly_solo' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white border-slate-200 text-slate-600'}`}
                    data-testid="sub-golf-solo">
                    {L('Solo · 999 CHF', 'Solo · 999 CHF')}
                  </button>
                  <button
                    onClick={() => setSubForm({ ...subForm, type: 'golf_yearly_group', price_per_lesson: '1499' })}
                    className={`px-3 py-2 rounded-lg text-sm font-bold border ${subForm.type === 'golf_yearly_group' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white border-slate-200 text-slate-600'}`}
                    data-testid="sub-golf-group">
                    {L('+ Guests · 1499 CHF', 'mit Gästen · 1499 CHF')}
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-2">
                  <div className="space-y-1">
                    <Label className="label-text text-xs">
                      {L('Total price (editable)', 'Gesamtpreis (editierbar)')} *
                    </Label>
                    <Input
                      type="number"
                      step="1"
                      value={subForm.price_per_lesson}
                      onChange={(e) => setSubForm({ ...subForm, price_per_lesson: e.target.value })}
                      className="bg-slate-50 border-slate-200"
                      data-testid="sub-golf-price"
                    />
                    <p className="text-[11px] text-slate-500">
                      {L('1 year unlimited access to Golf simulators from today', 'Ein Jahr unlimitierter Zugang zu Golf-Simulatoren ab heute')}
                    </p>
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-amber-800 font-semibold text-sm">{L('Customer pays', 'Kunde bezahlt')}</span>
                    <span className="text-2xl font-extrabold text-amber-700">
                      {(parseFloat(subForm.price_per_lesson) || 0).toFixed(0)} CHF
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-amber-700">
                    <span>{L('Valid until', 'Gültig bis')}</span>
                    <span className="font-mono">
                      {new Date(Date.now() + 365 * 24 * 3600 * 1000).toLocaleDateString(language === 'de' ? 'de-CH' : 'en-CH')}
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <>
            {/* Tariff tier + (Training only) participants */}
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="label-text text-xs">{L('Tariff', 'Tarif')}</Label>
                <div className="grid grid-cols-2 gap-1">
                  {[
                    { key: false, label: L('Off-peak', 'Off-peak') },
                    { key: true,  label: L('Peak', 'Peak') },
                  ].map(o => (
                    <button
                      key={String(o.key)}
                      onClick={() => setSubForm({
                        ...subForm,
                        is_peak: o.key,
                        price_per_lesson: String(computeSubDefaultPrice(subForm.type, o.key, subForm.participants)),
                      })}
                      className={`px-2 py-1.5 rounded-md text-xs font-bold border ${subForm.is_peak === o.key ? 'bg-slate-800 text-white border-slate-800' : 'bg-white border-slate-200 text-slate-600'}`}
                      data-testid={`sub-tier-${o.key ? 'peak' : 'offpeak'}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              {subForm.type === 'training' && (
                <div className="space-y-1">
                  <Label className="label-text text-xs">{L('Players', 'Teilnehmer')}</Label>
                  <div className="grid grid-cols-4 gap-1">
                    {[1, 2, 3, 4].map(n => (
                      <button
                        key={n}
                        onClick={() => setSubForm({
                          ...subForm,
                          participants: n,
                          price_per_lesson: String(computeSubDefaultPrice(subForm.type, subForm.is_peak, n)),
                        })}
                        className={`px-2 py-1.5 rounded-md text-xs font-bold border ${subForm.participants === n ? 'bg-blue-500 text-white border-blue-500' : 'bg-white border-slate-200 text-slate-600'}`}
                        data-testid={`sub-players-${n}`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1">
                <Label className="label-text text-xs">
                  {subForm.type === 'court'
                    ? L('Paid Slots', 'Bezahlte Std.')
                    : L('Paid Lessons', 'Bezahlte Lekt.')} *
                </Label>
                <Input type="number" min="1" value={subForm.paid_lessons} onChange={(e) => setSubForm({...subForm, paid_lessons: e.target.value})}
                  className="bg-slate-50 border-slate-200" data-testid="sub-paid-lessons" />
              </div>
              <div className="space-y-1">
                <Label className="label-text text-xs text-emerald-700">{L('Bonus (free)', 'Gratis')}</Label>
                <Input type="number" min="0" value={subForm.bonus_lessons} onChange={(e) => setSubForm({...subForm, bonus_lessons: e.target.value})}
                  className="bg-emerald-50 border-emerald-200" data-testid="sub-bonus-lessons" placeholder="0" />
              </div>
              <div className="space-y-1">
                <Label className="label-text text-xs">
                  {subForm.type === 'court'
                    ? L('Price/Slot', 'Preis/Std.')
                    : L('Price/Lesson', 'Preis/Lekt.')} *
                </Label>
                <Input type="number" step="0.5" value={subForm.price_per_lesson} onChange={(e) => setSubForm({...subForm, price_per_lesson: e.target.value})}
                  className="bg-slate-50 border-slate-200" data-testid="sub-price" />
              </div>
            </div>
            {(() => {
              const paid = parseInt(subForm.paid_lessons) || 0;
              const bonus = parseInt(subForm.bonus_lessons) || 0;
              const price = parseFloat(subForm.price_per_lesson) || 0;
              const total = paid + bonus;
              const cost = paid * price;
              return (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 space-y-1.5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-purple-700 font-semibold">
                      {subForm.type === 'court'
                        ? L('Total Slots', 'Stunden total')
                        : L('Total Lessons', 'Lektionen total')}
                    </span>
                    <span className="font-bold text-purple-900">
                      {paid}{bonus > 0 && <span className="text-emerald-600"> + {bonus}</span>} = {total}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-purple-700 font-semibold">{L('Customer pays', 'Kunde bezahlt')}</span>
                    <span className="text-2xl font-extrabold text-purple-700">{cost.toFixed(0)} CHF</span>
                  </div>
                </div>
              );
            })()}
              </>
            )}
            <div className="grid grid-cols-2 gap-2">
              {['cash', 'card'].map(m => (
                <button key={m} onClick={() => setSubForm({...subForm, payment_method: m})}
                  className={`px-3 py-2 rounded-lg text-sm font-bold border ${subForm.payment_method === m ? 'bg-slate-800 text-white border-slate-800' : 'bg-white border-slate-200 text-slate-600'}`} data-testid={`sub-pm-${m}`}>
                  {m === 'cash' ? L('Cash', 'Bar') : L('Card', 'Karte')}
                </button>
              ))}
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowSellSub(false)} className="flex-1 border-slate-200">{t('cancel')}</Button>
              <Button onClick={handleSellSubscription} disabled={subLoading} className="flex-1 bg-purple-500 text-white hover:bg-purple-600 font-bold" data-testid="confirm-sell-sub">
                {subLoading ? '...' : L('Sell', 'Verkaufen')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Subscription Detail Modal */}
      <Dialog open={!!subDetail} onOpenChange={(o) => { if (!o) setSubDetail(null); }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="sub-detail-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              {subDetail?.type === 'training' ? (
                <span className="inline-block px-2 py-0.5 rounded-md bg-blue-100 text-blue-700 text-xs font-bold">TRAINING</span>
              ) : subDetail?.type === 'court' ? (
                <span className="inline-block px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-xs font-bold">COURT</span>
              ) : null}
              {L('Subscription details', 'Abo-Details')}
            </DialogTitle>
            <DialogDescription>{subDetail?.user_name}</DialogDescription>
          </DialogHeader>
          {subDetailLoading || subDetail?.loading ? (
            <div className="py-8 text-center text-slate-400 text-sm">{L('Loading…', 'Lädt…')}</div>
          ) : subDetail ? (
            <div className="space-y-4 mt-2">
              {/* Progress */}
              {(() => {
                const total = subDetail.total_lessons || 0;
                const used = subDetail.used_lessons || 0;
                const remaining = Math.max(0, total - used);
                const pct = total ? (used / total) * 100 : 0;
                const exhausted = remaining <= 0;
                const pricePerLesson = subDetail.price_per_lesson || 0;
                return (
                  <div className={`rounded-xl p-4 ${exhausted ? 'bg-slate-50 border border-slate-200' : subDetail.type === 'training' ? 'bg-blue-50 border border-blue-200' : 'bg-emerald-50 border border-emerald-200'}`}>
                    <div className="flex items-end justify-between mb-2">
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">{L('Remaining', 'Verbleibend')}</p>
                        <p className={`text-3xl font-extrabold ${exhausted ? 'text-red-500' : 'text-slate-900'}`}>{remaining}<span className="text-lg font-semibold text-slate-500"> / {total}</span></p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">{L('Value left', 'Restwert')}</p>
                        <p className="text-2xl font-extrabold text-slate-800">{(remaining * pricePerLesson).toFixed(0)}<span className="text-sm font-semibold text-slate-500"> CHF</span></p>
                      </div>
                    </div>
                    <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div className={`h-full ${exhausted ? 'bg-red-400' : subDetail.type === 'training' ? 'bg-blue-500' : 'bg-emerald-500'} transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
                    </div>
                  </div>
                );
              })()}

              {/* Purchase info */}
              <div className="rounded-xl border border-slate-200 p-4 space-y-1.5 text-sm">
                <h4 className="font-bold text-slate-800 mb-2 uppercase text-xs tracking-wide">{L('Purchase', 'Kauf')}</h4>
                <div className="flex justify-between"><span className="text-slate-500">{L('Purchased at', 'Gekauft am')}</span><span className="font-semibold">{subDetail.purchased_at ? new Date(subDetail.purchased_at).toLocaleString(language === 'de' ? 'de-CH' : 'en-CH', { dateStyle: 'medium', timeStyle: 'short' }) : '-'}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">{L('Sold by', 'Verkauft von')}</span><span className="font-semibold">{subDetail.purchased_by_name || '-'}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">{L('Total price', 'Gesamtpreis')}</span><span className="font-semibold">{(subDetail.total_price || 0).toFixed(2)} CHF</span></div>
                <div className="flex justify-between">
                  <span className="text-slate-500">
                    {subDetail.type === 'court'
                      ? L('Price per court hour', 'Preis pro Court-Stunde')
                      : L('Price per lesson', 'Preis pro Lektion')}
                  </span>
                  <span className="font-semibold">{(subDetail.price_per_lesson || 0).toFixed(2)} CHF</span>
                </div>
                {subDetail.sale_payment_method && (
                  <div className="flex justify-between"><span className="text-slate-500">{L('Payment', 'Zahlung')}</span><span className="font-semibold uppercase">{subDetail.sale_payment_method === 'cash' ? L('Cash', 'Bar') : subDetail.sale_payment_method === 'card' ? L('Card', 'Karte') : subDetail.sale_payment_method}</span></div>
                )}
                <div className="flex justify-between"><span className="text-slate-500">{L('Status', 'Status')}</span>
                  <span className={`font-semibold ${subDetail.status === 'exhausted' ? 'text-red-500' : 'text-emerald-600'}`}>
                    {subDetail.status === 'exhausted' ? L('Exhausted', 'Aufgebraucht') : L('Active', 'Aktiv')}
                  </span>
                </div>
              </div>

              {/* Usage history */}
              <div className="rounded-xl border border-slate-200 p-4">
                <h4 className="font-bold text-slate-800 mb-3 uppercase text-xs tracking-wide">
                  {L('Usage history', 'Nutzungsverlauf')} <span className="text-slate-400 font-normal">({(subDetail.usage_log || []).length})</span>
                </h4>
                {(subDetail.usage_log || []).length === 0 ? (
                  <p className="text-sm text-slate-400 italic">{L('No usage yet', 'Noch keine Nutzung')}</p>
                ) : (
                  <div className="space-y-2">
                    {subDetail.usage_log.map((u, idx) => (
                      <div key={idx} className={`flex items-start justify-between gap-3 p-2 rounded-lg ${u.refunded ? 'bg-slate-50 opacity-70' : 'bg-slate-50/50 hover:bg-slate-100'}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-800">
                            #{idx + 1} · {u.booking_date ? (() => {
                              const d = new Date(u.booking_date + 'T00:00:00');
                              return d.toLocaleDateString(language === 'de' ? 'de-CH' : 'en-CH', { weekday: 'short', day: '2-digit', month: 'short' });
                            })() : L('Deleted booking', 'Gelöschte Buchung')}
                            {u.booking_start_time && <span className="ml-1.5 text-slate-500 font-normal">@ {u.booking_start_time}</span>}
                            {u.refunded && <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-bold">REFUND</span>}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {u.booking_court_name && <span>{u.booking_court_name}</span>}
                            {u.booking_trainer_name && <span> · {L('Trainer', 'Trainer')}: {u.booking_trainer_name}</span>}
                            {u.booking_deleted && <span className="italic">{L('Booking was deleted', 'Buchung wurde gelöscht')}</span>}
                          </p>
                        </div>
                        <div className="text-right text-[10px] text-slate-400 font-mono whitespace-nowrap">
                          {u.used_at ? new Date(u.used_at).toLocaleDateString(language === 'de' ? 'de-CH' : 'en-CH', { day: '2-digit', month: '2-digit', year: '2-digit' }) : ''}
                          <br />
                          <span>{L('by', 'von')} {u.used_by_name || '-'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-2">
                <Button variant="outline" onClick={() => setSubDetail(null)} className="border-slate-200">{L('Close', 'Schliessen')}</Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* CSV Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="import-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <FileText size={22} className="text-blue-600" weight="duotone" />
              {L('Import Users', 'Benutzer importieren')}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {importResult 
                ? `${importResult.imported} ${L('imported', 'importiert')}, ${importResult.skipped} ${L('skipped', 'übersprungen')}`
                : `${importPreview.length} ${L('users found in CSV', 'Benutzer in CSV gefunden')}`}
            </DialogDescription>
          </DialogHeader>
          {!importResult ? (
            <div className="space-y-4 mt-2">
              <div className="bg-blue-50 rounded-lg p-3 text-xs text-blue-700">
                {L('Imported clients get no shared password — send them the activation link (Migration) so they set their own.', 'Importierte Kunden erhalten kein gemeinsames Passwort – sie setzen ihr eigenes über den Aktivierungslink (Migration).')}
              </div>
              <div className="border rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">#</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">{t('name')}</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">{t('email')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importPreview.slice(0, 50).map((u, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="py-1.5 px-3 text-slate-400 text-xs">{i + 1}</td>
                        <td className="py-1.5 px-3 text-slate-800">{u.name}</td>
                        <td className="py-1.5 px-3 text-slate-500">{u.email}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowImportModal(false)} className="flex-1 border-slate-200 text-slate-600">{t('cancel')}</Button>
                <Button onClick={executeImport} disabled={importLoading}
                  className="flex-1 bg-blue-600 text-white hover:bg-blue-700 font-bold gap-2" data-testid="confirm-import-btn">
                  {importLoading ? L('Importing...', 'Importiere...') : <><UploadSimple size={16} /> {L(`Import ${importPreview.length}`, `${importPreview.length} importieren`)}</>}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-emerald-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-emerald-600">{importResult.imported}</p>
                  <p className="text-xs text-emerald-700">{L('Imported', 'Importiert')}</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-amber-600">{importResult.skipped}</p>
                  <p className="text-xs text-amber-700">{L('Skipped', 'Übersprungen')}</p>
                </div>
              </div>
              <Button onClick={() => setShowImportModal(false)} className="w-full bg-slate-800 text-white hover:bg-slate-900 font-bold">
                {L('Done', 'Fertig')}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
